import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Star, Eye, EyeOff, Pencil, Trash2, Plus } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const EMPTY = { name: "", location: "", text: "", rating: 5, featured: false, visible: true };

export default function TestimonialsManager() {
  const qc = useQueryClient();
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);
  const { data, isLoading } = useQuery({
    queryKey: ["admin-testimonials"],
    queryFn: async () => (await api.get("/admin/testimonials")).data,
  });

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["admin-testimonials"] });
    qc.invalidateQueries({ queryKey: ["testimonials"] });
  };

  const save = async (ev) => {
    ev.preventDefault();
    try {
      if (editing) await api.patch(`/admin/testimonials/${editing}`, form);
      else await api.post("/admin/testimonials", form);
      toast.success(editing ? "Review updated" : "Review added");
      setForm(EMPTY);
      setEditing(null);
      refresh();
    } catch {
      toast.error("Failed to save review");
    }
  };

  const patch = async (id, body) => {
    try {
      await api.patch(`/admin/testimonials/${id}`, body);
      refresh();
    } catch {
      toast.error("Update failed");
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/admin/testimonials/${id}`);
      toast.success("Review deleted");
      refresh();
    } catch {
      toast.error("Delete failed");
    }
  };

  const inputCls = "w-full border border-sandline bg-white px-3 py-2.5 text-sm focus:outline-none focus:border-orange";

  return (
    <div data-testid="admin-testimonials">
      <h1 className="font-heading font-bold text-2xl tracking-tight mb-6">Testimonials Manager</h1>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <form onSubmit={save} className="bg-white border border-sandline p-6 space-y-4 h-fit" data-testid="testimonial-form">
          <h2 className="font-heading font-bold tracking-tight">{editing ? "Edit Review" : "Add Review"}</h2>
          <input required placeholder="Customer name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="testimonial-name" className={inputCls} />
          <input placeholder="Location (e.g. Kendall, FL)" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} data-testid="testimonial-location" className={inputCls} />
          <textarea required rows={4} placeholder="Review text" value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })} data-testid="testimonial-text" className={`${inputCls} resize-none`} />
          <div className="flex items-center gap-4">
            <label className="text-xs font-bold uppercase tracking-widest text-slateblue">Rating</label>
            <select value={form.rating} onChange={(e) => setForm({ ...form, rating: Number(e.target.value) })} data-testid="testimonial-rating" className={inputCls}>
              {[5, 4, 3, 2, 1].map((r) => <option key={r} value={r}>{r} stars</option>)}
            </select>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} data-testid="testimonial-featured" className="accent-orange w-4 h-4" />
            Featured on homepage
          </label>
          <div className="flex gap-2">
            <button type="submit" data-testid="testimonial-save" className="flex-1 inline-flex items-center justify-center gap-2 bg-orange hover:bg-orange-bright text-white text-xs font-bold uppercase tracking-widest px-4 py-3 transition-colors duration-200">
              <Plus size={14} /> {editing ? "Save Changes" : "Add Review"}
            </button>
            {editing && (
              <button type="button" onClick={() => { setEditing(null); setForm(EMPTY); }} data-testid="testimonial-cancel"
                className="px-4 py-3 border border-sandline text-xs font-bold uppercase tracking-widest hover:border-navy transition-colors duration-200">
                Cancel
              </button>
            )}
          </div>
        </form>

        <div className="lg:col-span-2 space-y-3">
          {isLoading ? (
            <p className="text-slateblue text-sm">Loading…</p>
          ) : (data || []).map((item) => (
            <div key={item.id} data-testid={`testimonial-row-${item.id.slice(0, 8)}`}
              className={`bg-white border p-5 ${item.visible ? "border-sandline" : "border-sandline opacity-55"}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2">
                    <p className="font-bold text-sm">{item.name}</p>
                    <span className="flex gap-0.5">
                      {Array.from({ length: item.rating }).map((_, i) => <Star key={i} size={11} className="text-orange" fill="currentColor" />)}
                    </span>
                    {item.featured && <span className="text-[10px] font-bold uppercase tracking-wide bg-orange/10 text-orange px-1.5 py-0.5">Featured</span>}
                    {!item.visible && <span className="text-[10px] font-bold uppercase tracking-wide bg-offwhite-dark text-slateblue px-1.5 py-0.5">Hidden</span>}
                  </div>
                  <p className="text-xs text-slateblue mt-0.5">{item.location}</p>
                </div>
                <div className="flex gap-1">
                  <button onClick={() => patch(item.id, { visible: !item.visible })} data-testid={`testimonial-hide-${item.id.slice(0, 8)}`} title={item.visible ? "Hide" : "Show"}
                    className="p-2 text-slateblue hover:text-orange transition-colors duration-200">
                    {item.visible ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                  <button onClick={() => { setEditing(item.id); setForm({ name: item.name, location: item.location, text: item.text, rating: item.rating, featured: item.featured, visible: item.visible }); }} data-testid={`testimonial-edit-${item.id.slice(0, 8)}`}
                    className="p-2 text-slateblue hover:text-orange transition-colors duration-200">
                    <Pencil size={16} />
                  </button>
                  <button onClick={() => remove(item.id)} data-testid={`testimonial-delete-${item.id.slice(0, 8)}`}
                    className="p-2 text-slateblue hover:text-red-500 transition-colors duration-200">
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
              <p className="mt-2 text-sm text-navy/85">{item.text}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
